import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../lib/utils";

export interface DrawerProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Trigger element (cloned with proper aria wiring via Radix Slot). */
  trigger?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  side?: "bottom" | "left" | "right";
  className?: string;
  children?: React.ReactNode;
  "aria-label"?: string;
}

/**
 * Mobile-friendly sheet built on the Dialog primitives: slides in from the
 * bottom (default) or a side, with a grab-handle, focus trap and Escape close.
 */
const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  (
    {
      open,
      defaultOpen,
      onOpenChange,
      trigger,
      title,
      description,
      side = "bottom",
      className,
      children,
      ...aria
    },
    ref,
  ) => {
    const labelled = title ?? aria["aria-label"];
    return (
      <DialogPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
        {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in dark:bg-black/60" />
          <DialogPrimitive.Content
            ref={ref}
            aria-label={typeof labelled === "string" ? labelled : undefined}
            className={cn(
              "fixed z-50 flex flex-col border-border bg-card text-card-foreground shadow-panel",
              side === "bottom" &&
                "inset-x-0 bottom-0 max-h-[85vh] rounded-t-xl border-t border-x motion-safe:data-[state=open]:animate-fade-up",
              side === "left" &&
                "inset-y-0 left-0 w-[min(22rem,90vw)] border-r motion-safe:data-[state=open]:animate-slide-in",
              side === "right" &&
                "inset-y-0 right-0 w-[min(22rem,90vw)] border-l motion-safe:data-[state=open]:animate-slide-in",
              className,
            )}
          >
            {side === "bottom" ? (
              <span
                aria-hidden="true"
                className="mx-auto mt-2 h-1 w-10 rounded-full bg-muted-foreground/40"
              />
            ) : null}
            <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-4">
              <div className="flex flex-col gap-1">
                {title ? (
                  <DialogPrimitive.Title className="font-display text-base font-semibold tracking-tight">
                    {title}
                  </DialogPrimitive.Title>
                ) : null}
                {description ? (
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {description}
                  </DialogPrimitive.Description>
                ) : null}
              </div>
              <DialogPrimitive.Close className="rounded-lg p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <X className="size-4" aria-hidden="true" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2">{children}</div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  },
);
Drawer.displayName = "Drawer";

export { Drawer };
